<template>
  <div class="lpg">
    <div v-if="!showOtherForm" class="lpg-grid" role="radiogroup" :aria-label="$t('onboarding.newStudent.gridAriaLabel')">
      <button
        v-for="(lang, code) in displayLanguages"
        :key="code"
        type="button"
        class="lpg-card"
        :class="{
          'lpg-card--active': selectedCode === code,
          'lpg-card--disabled': isDisabled(code),
        }"
        :disabled="isDisabled(code)"
        :aria-checked="selectedCode === code"
        :aria-disabled="isDisabled(code) ? 'true' : 'false'"
        role="radio"
        @click="onPick(code)"
      >
        <div class="lpg-flag-wrap">
          <img
            :src="flagsStore.getFlag(code)"
            :alt="lang.altText"
            class="lpg-flag"
          />
          <span v-if="lang.dialectLabel" class="lpg-dialect-label">{{
            lang.dialectLabel
          }}</span>
        </div>
        <div class="lpg-text">
          <span class="lpg-primary" :lang="lang.nativeScriptLang">{{
            lang.nativeScriptName
          }}</span>
          <span class="lpg-secondary">{{ secondaryLabel(code) }}</span>
        </div>
        <span v-if="isDisabled(code)" class="lpg-disabled-badge">{{
          $t(disabledBadgeKey(code))
        }}</span>
      </button>

      <button
        v-if="showOtherCard"
        type="button"
        class="lpg-card lpg-card--other"
        :class="{ 'lpg-card--other-requested': !!existingTypedLanguage }"
        @click="openOtherForm"
      >
        <div class="lpg-other-icon" aria-hidden="true">{{ existingTypedLanguage ? "✓" : "+" }}</div>
        <div class="lpg-text">
          <span class="lpg-primary">{{
            $t("onboarding.newStudent.otherCard.label")
          }}</span>
          <span class="lpg-secondary">{{
            existingTypedLanguage
              ? $t("onboarding.newStudent.otherCard.requestedSubtitle", {
                  language: existingTypedLanguage,
                })
              : $t("onboarding.newStudent.otherCard.subtitle")
          }}</span>
        </div>
      </button>
    </div>

    <div v-else-if="otherSubmitted" class="lpg-other-confirmation">
      <div class="lpg-other-confirmation-icon" aria-hidden="true">✓</div>
      <h3 class="lpg-other-confirmation-title">
        {{ $t("onboarding.newStudent.otherCard.confirmationTitle") }}
      </h3>
      <p class="lpg-other-confirmation-body">
        {{ $t("onboarding.newStudent.otherCard.confirmation") }}
      </p>
    </div>

    <form v-else class="lpg-other-form" @submit.prevent="submitOther">
      <button
        type="button"
        class="lpg-other-form-back"
        @click="closeOtherForm"
        :aria-label="$t('onboarding.wizard.back')"
      >
        ← {{ $t("onboarding.wizard.back") }}
      </button>
      <h3 class="lpg-other-form-title">
        {{ $t("onboarding.newStudent.otherCard.heading") }}
      </h3>
      <div v-if="userEmail" class="mb-3">
        <label for="lpg-other-email" class="form-label">{{
          $t("onboarding.newStudent.otherCard.emailLabel")
        }}</label>
        <input
          id="lpg-other-email"
          type="email"
          class="form-control"
          :value="userEmail"
          readonly
          autocomplete="email"
        />
      </div>
      <div class="mb-3">
        <label for="lpg-other-language" class="form-label">{{
          $t("onboarding.newStudent.otherCard.languageLabel")
        }}</label>
        <input
          id="lpg-other-language"
          type="text"
          inputmode="text"
          class="form-control"
          v-model="typedLanguage"
          maxlength="100"
          required
          :placeholder="
            $t('onboarding.newStudent.otherCard.languagePlaceholder')
          "
        />
      </div>
      <div v-if="otherError" class="lpg-other-form-error" role="alert">
        {{ otherError }}
      </div>
      <button
        type="submit"
        class="btn btn-primary lpg-other-form-submit"
        :disabled="!canSubmitOther || isSubmittingOther"
      >
        <span v-if="!isSubmittingOther">{{
          $t("onboarding.newStudent.otherCard.submitButton")
        }}</span>
        <span v-else>{{
          $t("onboarding.newStudent.otherCard.submitting")
        }}</span>
      </button>
    </form>
  </div>
</template>

<script>
import { Languages } from "@/data/Language.js";
import { flagsStore } from "@/store/countryFlags.js";
import { isSameBaseLanguage } from "@/locales/index.js";
import { useHaptics } from "@/composables/useHaptics.js";

export default {
  name: "LanguagePickerGrid",
  props: {
    selectedCode: {
      type: String,
      default: null,
    },
    geoLikelyCode: {
      type: String,
      default: null,
    },
    disabledCodes: {
      type: Array,
      default: () => [],
    },
    disabledBaseCodes: {
      type: Array,
      default: () => [],
    },
    activeCode: {
      type: String,
      default: null,
    },
    disabledBaseBadgeKey: {
      type: String,
      default: "languagePicker.nativeLanguageBadge",
    },
    showOtherCard: {
      type: Boolean,
      default: false,
    },
    userEmail: {
      type: String,
      default: "",
    },
    isSubmittingOther: {
      type: Boolean,
      default: false,
    },
    otherError: {
      type: String,
      default: "",
    },
    otherSubmitted: {
      type: Boolean,
      default: false,
    },
    existingTypedLanguage: {
      type: String,
      default: "",
    },
  },
  emits: ["pick", "other-submit", "other-form-toggle"],
  setup() {
    return { haptics: useHaptics() };
  },
  data() {
    return {
      flagsStore,
      showOtherForm: false,
      typedLanguage: "",
    };
  },
  computed: {
    displayLanguages() {
      const entries = Object.entries(Languages);
      const geo = this.geoLikelyCode;
      const sorted = entries.sort((a, b) => {
        if (geo) {
          if (a[0] === geo) return -1;
          if (b[0] === geo) return 1;
        }
        return a[1].nativeScriptName.localeCompare(b[1].nativeScriptName);
      });
      return Object.fromEntries(sorted);
    },
    canSubmitOther() {
      return this.typedLanguage.trim().length > 0;
    },
  },
  watch: {
    otherSubmitted(submitted) {
      if (submitted) {
        this.typedLanguage = "";
      }
    },
  },
  methods: {
    isDisabled(code) {
      // The active learning language is never pickable. Callers already pass it
      // inside disabledCodes, but keeping the rule here means the badge and the
      // disabled state can't disagree if that ever desyncs.
      if (code === this.activeCode) return true;
      if (this.disabledCodes.includes(code)) return true;
      for (const base of this.disabledBaseCodes) {
        if (isSameBaseLanguage(code, base)) return true;
      }
      return false;
    },
    // Badge for a disabled card, by disable reason (first match wins): the
    // active learning language, another already-held language, or the
    // base-code match (native language at most call sites; the caller
    // overrides the key where the base-disabled language is something else).
    disabledBadgeKey(code) {
      if (code === this.activeCode) return "languagePicker.activeLanguageBadge";
      if (this.disabledCodes.includes(code)) return "languagePicker.alreadyLearningBadge";
      return this.disabledBaseBadgeKey;
    },
    secondaryLabel(code) {
      const translation = this.$t(`languages.${code}`);
      if (translation === Languages[code]?.nativeScriptName) {
        return Languages[code]?.name;
      }
      return translation;
    },
    onPick(code) {
      if (this.isDisabled(code)) return;
      // Completed selection: confirm with a light haptic on native platforms.
      this.haptics.impactLight();
      this.$emit("pick", code);
    },
    openOtherForm() {
      this.showOtherForm = true;
      if (!this.typedLanguage && this.existingTypedLanguage) {
        this.typedLanguage = this.existingTypedLanguage;
      }
      this.$emit("other-form-toggle", true);
    },
    closeOtherForm() {
      this.showOtherForm = false;
      this.typedLanguage = "";
      this.$emit("other-form-toggle", false);
    },
    submitOther() {
      const trimmed = this.typedLanguage.trim();
      if (!trimmed) return;
      this.$emit("other-submit", { typedLanguage: trimmed });
    },
  },
};
</script>

<style scoped>
.lpg {
  width: 100%;
}

.lpg-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--al-space-md);
  margin-bottom: var(--al-space-sm);
}

@media (min-width: 480px) {
  .lpg-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 768px) {
  .lpg-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.lpg-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 0.875rem 0.5rem;
  border-radius: var(--al-radius);
  background-color: var(--al-surface-sunken);
  border: 2px solid transparent;
  cursor: pointer;
  transition: transform var(--al-dur-short) var(--al-ease-move),
    box-shadow var(--al-dur-short) var(--al-ease-move),
    background-color var(--al-dur-short) var(--al-ease-move),
    border-color var(--al-dur-short) var(--al-ease-move);
  text-align: center;
  width: 100%;
  min-width: 0;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  position: relative;
  min-height: 110px;
}

.lpg-card:hover:not(.lpg-card--disabled):not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--al-shadow-sm);
}

.lpg-card:focus-visible {
  outline: 3px solid var(--al-primary);
  outline-offset: 2px;
}

/* Selected-chip recipe: primary border + primary-soft bg + ink/gray-700 text
   (not primary text on the soft bg, which measures ~2.7:1 and fails AA). */
.lpg-card--active {
  border-color: var(--al-primary);
  background-color: var(--al-primary-soft);
  transform: translateY(-2px);
  box-shadow: 0 4px 6px color-mix(in srgb, var(--al-primary) 20%, transparent);
}

.lpg-card--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.lpg-flag-wrap {
  position: relative;
  width: 2.75rem;
  height: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lpg-flag {
  width: 100%;
  height: auto;
  border-radius: 0.25rem;
  border: 1px solid var(--al-border);
  object-fit: cover;
  display: block;
}

.lpg-dialect-label {
  position: absolute;
  bottom: -6px;
  right: -8px;
  background-color: var(--al-gray-700);
  color: var(--al-white);
  font-size: 0.625rem;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 0.25rem;
  line-height: 1.2;
  letter-spacing: 0.02em;
}

.lpg-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  width: 100%;
  min-width: 0;
}

.lpg-primary {
  font-size: 1rem;
  font-weight: 600;
  color: var(--al-text);
  line-height: 1.25;
  width: 100%;
  word-break: keep-all;
  overflow-wrap: break-word;
}

.lpg-secondary {
  font-size: 0.75rem;
  color: var(--al-text-muted);
  line-height: 1.25;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.lpg-disabled-badge {
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  font-size: 0.5625rem;
  color: var(--al-text-muted);
  font-style: italic;
  background-color: color-mix(in srgb, var(--al-surface-raised) 85%, transparent);
  padding: 1px 4px;
  border-radius: 0.25rem;
  white-space: nowrap;
}

.lpg-card--other {
  border-style: dashed;
  border-color: var(--al-border);
  background-color: var(--al-surface-sunken);
}

.lpg-card--other:hover {
  border-color: var(--al-primary);
  background-color: var(--al-primary-soft);
}

/* AA: --al-success text on white/soft backgrounds is ~2.5:1 (fails), so the
   requested-waitlist icon uses --al-success-dark instead. */
.lpg-card--other-requested {
  border-style: solid;
  border-color: var(--al-success);
  background-color: color-mix(in srgb, var(--al-success) 10%, var(--al-surface-raised));
}

.lpg-card--other-requested .lpg-other-icon {
  background-color: color-mix(in srgb, var(--al-success) 15%, var(--al-surface-raised));
  color: var(--al-success-dark);
}

.lpg-other-icon {
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--al-surface-sunken);
  color: var(--al-text);
  border-radius: 50%;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1;
}

.lpg-other-form {
  padding: 0.5rem 0;
}

/* AA: --al-primary text on white is ~3.33:1 (fails for normal-size text), so
   this link uses --al-primary-hover (~4.67:1) instead. */
.lpg-other-form-back {
  background: none;
  border: none;
  color: var(--al-primary-hover);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-bottom: 0.75rem;
}

.lpg-other-form-back:hover {
  text-decoration: underline;
}

.lpg-other-form-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--al-text);
  margin-bottom: 1rem;
}

.lpg-other-form-error {
  color: var(--al-danger);
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
}

.lpg-other-form-submit {
  width: 100%;
}

.lpg-other-confirmation {
  text-align: center;
  padding: 1.5rem 1rem;
}

.lpg-other-confirmation-icon {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background-color: color-mix(in srgb, var(--al-success) 15%, var(--al-surface-raised));
  color: var(--al-success-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 700;
}

.lpg-other-confirmation-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--al-text);
  margin-bottom: 0.5rem;
}

.lpg-other-confirmation-body {
  color: var(--al-text-muted);
  font-size: 0.9375rem;
  line-height: 1.5;
}
</style>
